"use client";
import { useState } from "react";
import { Temporal } from "temporal-polyfill";
import { cn } from "../lib/utils";
import { Button } from "./button";
import { Icon } from "./icon";

interface MonthRangePickerProps {
  maxMonth?: Temporal.PlainDate;
  selectedMonthRange: [Temporal.PlainDate, Temporal.PlainDate] | undefined;
  onMonthRangeSelected: (value: [Temporal.PlainDate, Temporal.PlainDate] | undefined) => void;
}

export function MonthRangePicker({ maxMonth, selectedMonthRange, onMonthRangeSelected }: MonthRangePickerProps) {
  const now = Temporal.Now.plainDateISO();
  const [currentYear, setCurrentYear] = useState(now.year);

  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  const handleYearChange = (increment: number) => {
    const newYear = currentYear + increment;
    if (!maxMonth || newYear <= maxMonth.year) {
      setCurrentYear(newYear);
    }
  };

  const handleMonthSelect = (month: number) => {
    const selectedDate = Temporal.PlainDate.from({
      year: currentYear,
      month: month + 1,
      day: 1,
    });

    if (maxMonth && Temporal.PlainDate.compare(selectedDate, maxMonth) > 0) {
      return;
    }

    if (!selectedMonthRange) {
      onMonthRangeSelected([selectedDate, selectedDate]);
    } else {
      const [start, end] = selectedMonthRange;
      if (
        Temporal.PlainDate.compare(selectedDate, start) === 0 ||
        Temporal.PlainDate.compare(selectedDate, end) === 0
      ) {
        // If clicking on start or end date, reset selection
        onMonthRangeSelected(undefined);
      } else if (Temporal.PlainDate.compare(selectedDate, start) < 0) {
        // If before start, set as new start
        onMonthRangeSelected([selectedDate, end]);
      } else if (Temporal.PlainDate.compare(selectedDate, end) > 0) {
        // If after end, set as new end
        onMonthRangeSelected([start, selectedDate]);
      } else {
        // If within range, set as new start
        onMonthRangeSelected([selectedDate, selectedDate]);
      }
    }
  };

  const isMonthSelected = (month: number) => {
    if (!selectedMonthRange) return false;

    const date = Temporal.PlainDate.from({
      year: currentYear,
      month: month + 1,
      day: 1,
    });

    const [start, end] = selectedMonthRange;
    return Temporal.PlainDate.compare(date, start) >= 0 && Temporal.PlainDate.compare(date, end) <= 0;
  };

  const isStartMonth = (month: number) => {
    if (!selectedMonthRange) return false;

    const [start] = selectedMonthRange;
    return start.year === currentYear && start.month === month + 1;
  };

  const isEndMonth = (month: number) => {
    if (!selectedMonthRange) return false;

    const [, end] = selectedMonthRange;
    return end.year === currentYear && end.month === month + 1;
  };

  return (
    <div className="w-full max-w-sm mx-auto">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b">
          <Button
            variant="ghost"
            onClick={() => handleYearChange(-1)}
            className="p-2 rounded-full hover:opacity-50 disabled:cursor-not-allowed"
          >
            <Icon name="arrow-left" iconSize="16px" />
          </Button>
          <span className="text-lg font-medium">{currentYear}</span>
          <Button
            variant="ghost"
            onClick={() => handleYearChange(1)}
            className="p-2 rounded-full hover:opacity-50 disabled:cursor-not-allowed"
            disabled={maxMonth && currentYear >= maxMonth.year}
          >
            <Icon name="arrow-right" iconSize="16px" />
          </Button>
        </div>

        <div className="grid grid-cols-4 gap-4 p-4">
          {months.map((month, index) => {
            const isDisabled =
              maxMonth &&
              (currentYear > maxMonth.year || (currentYear === maxMonth.year && index + 1 > maxMonth.month));

            return (
              <Button
                key={month}
                variant="ghost"
                onClick={() => !isDisabled && handleMonthSelect(index)}
                disabled={isDisabled}
                className={cn(
                  "py-3 px-4 rounded-full text-center font-medium transition-colors",
                  isStartMonth(index) || isEndMonth(index)
                    ? "bg-black text-white hover:bg-black"
                    : isMonthSelected(index)
                      ? "bg-backgroundDark hover:bg-backgroundDark"
                      : isDisabled
                        ? "text-textSubtle cursor-not-allowed"
                        : "hover:bg-backgroundLight",
                )}
              >
                {month}
              </Button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
